import type { TFunction } from 'i18next'

/**
 * «Доступно обновление» с кнопкой перезагрузки.
 *
 * Новый service worker встаёт на место старого сразу (`skipWaiting`),
 * но открытая вкладка продолжает жить на прежнем наборе файлов:
 * оболочка уже загружена, и подменять её под руками у человека нельзя —
 * он в середине создания персонажа. Поэтому не перезагружаем сами,
 * а говорим и даём кнопку.
 *
 * Полоса, а не диалог: обновление — не событие, требующее ответа.
 * Не нажали — работает дальше, обновится в следующий заход.
 *
 * Рисуется узлами, а не React-компонентом, по той же причине, что
 * и подвал с версией: сообщение приходит от регистрации worker'а,
 * то есть снаружи дерева, и тащить сигнал через `App` ради одной
 * полосы значит завести состояние, которое больше никому не нужно.
 */

export type UpdateBannerLabels = {
  readonly available: string
  readonly reload: string
}

export function updateBannerLabels(t: TFunction): UpdateBannerLabels {
  return { available: t('update.available'), reload: t('update.reload') }
}

export function renderUpdateBanner(
  root: ParentNode & { append: (node: Node) => void },
  labels: UpdateBannerLabels,
  document: Document,
  reload: () => void,
): HTMLElement {
  const banner = document.createElement('div')
  banner.className = 'app-update'
  // Голосовой доступ должен объявить это, но не перебивая: полоса
  // не требует немедленного ответа.
  banner.setAttribute('role', 'status')
  banner.dataset['testid'] = 'update-banner'

  const text = document.createElement('span')
  text.textContent = labels.available
  banner.append(text)

  const button = document.createElement('button')
  button.type = 'button'
  button.className = 'app-button app-button-quiet'
  button.textContent = labels.reload
  button.addEventListener('click', reload)
  banner.append(button)

  root.append(banner)
  return banner
}
